// Banner shown above the editor when the app opens on a `/s/<id>`
// share link. The shared code is already loaded into the editor buffer
// as an unsaved draft; this strip carries the attribution + the actions
// a visitor wants: play it, fork it (sign-in gated), or copy the link.
//
// Three states: loading while the fetch is in flight, found (the patch
// resolved), and notfound (private, deleted, or a bogus id — the app
// stays fully usable, this just explains why nothing loaded).

import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { getParentChain, type LineagePatch, type SharedPatch } from '../cloud';
import { buildShareUrl } from '../share/route';

export type SharedBannerState =
  | { kind: 'loading' }
  | { kind: 'found'; patch: SharedPatch }
  | { kind: 'notfound' };

type Props = {
  state: SharedBannerState;
  signedIn: boolean;
  forking: boolean;
  onPlay: () => void;
  onFork: () => void;
  onCopyStatus: (msg: string, level?: 'ok' | 'warn' | 'err' | '') => void;
  onDismiss: () => void;
};

export function SharedSongBanner({
  state,
  signedIn,
  forking,
  onPlay,
  onFork,
  onCopyStatus,
  onDismiss,
}: Props) {
  // Ancestor chain (root first, capped at 5 hops inside getParentChain)
  // — remix lineage rendered as content. Decorative: a failed read just
  // leaves the chain empty.
  const patchId = state.kind === 'found' ? state.patch.id : null;
  const [chain, setChain] = useState<LineagePatch[]>([]);
  useEffect(() => {
    setChain([]);
    if (!patchId) return;
    let cancelled = false;
    getParentChain(patchId)
      .then((ancestors) => {
        if (!cancelled) setChain(ancestors);
      })
      .catch(() => {
        // Lineage is decorative; the banner works without it.
      });
    return () => {
      cancelled = true;
    };
  }, [patchId]);

  return (
    <div className="border-b border-zinc-800 bg-gradient-to-b from-zinc-900/90 to-zinc-950 px-4 py-2.5 flex items-center gap-3 safe-pl safe-pr">
      <span
        aria-hidden="true"
        className="text-[9px] tracking-[0.32em] uppercase font-display text-yellow-300 shrink-0"
      >
        Shared
      </span>

      <div className="flex-1 min-w-0">
        {state.kind === 'loading' && (
          <div className="text-[11px] text-zinc-500 tracking-wide">
            Loading shared song…
          </div>
        )}
        {state.kind === 'notfound' && (
          <div className="text-[11px] text-zinc-400 tracking-wide truncate">
            This song isn't available — it may be private or removed. You can
            still explore everything else.
          </div>
        )}
        {state.kind === 'found' && (
          <>
            <div className="text-[12px] text-zinc-100 font-body truncate">
              {state.patch.title || '(untitled)'}
            </div>
            <div className="text-[9px] text-zinc-500 tracking-wider tabular-nums flex items-center gap-2">
              <span>@{state.patch.authorHandle}</span>
              <span aria-hidden="true">·</span>
              <span>▶ {state.patch.playsCount}</span>
            </div>
            {chain.length > 0 && (
              <div className="text-[9px] text-zinc-500 tracking-wide truncate">
                <span aria-hidden="true">⑂ </span>
                {chain.map((ancestor) => (
                  <Fragment key={ancestor.patchId}>
                    <a
                      href={buildShareUrl(ancestor.patchId)}
                      title={`Open "${ancestor.title}" by @${ancestor.authorHandle}`}
                      className="text-zinc-300 hover:text-cyan-300 underline decoration-zinc-700 underline-offset-2"
                    >
                      {ancestor.title}
                    </a>
                    <span> by @{ancestor.authorHandle}</span>
                    <span aria-hidden="true"> → </span>
                  </Fragment>
                ))}
                <span className="text-zinc-400">this</span>
              </div>
            )}
          </>
        )}
      </div>

      {state.kind === 'found' && (
        <div className="flex items-center gap-1.5 shrink-0">
          <CopyLinkButton
            url={buildShareUrl(state.patch.id)}
            onStatus={onCopyStatus}
          />
          <button
            type="button"
            onClick={onFork}
            disabled={forking || !signedIn}
            title={signedIn ? 'Fork into your library' : 'Sign in to fork'}
            className="text-[10px] uppercase tracking-[0.18em] px-2.5 py-1.5 rounded-sm bg-zinc-800 border border-zinc-700 text-zinc-200 hover:bg-zinc-700 disabled:opacity-30"
          >
            {forking ? '…' : '⑂ Fork'}
          </button>
          <button
            type="button"
            onClick={onPlay}
            title="Play this shared song"
            className="text-[10px] uppercase tracking-[0.18em] px-2.5 py-1.5 rounded-sm bg-yellow-300 border border-yellow-300 text-zinc-950 font-semibold hover:brightness-95"
          >
            ▶ Play
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss shared song banner"
        title="Dismiss"
        className="shrink-0 text-zinc-500 hover:text-zinc-200 text-lg leading-none px-1"
      >
        ✕
      </button>
    </div>
  );
}

// Local copy of the community feed's copy-link affordance — a brief
// "copied" flip on the button plus a status-bar note.
function CopyLinkButton({
  url,
  onStatus,
}: {
  url: string;
  onStatus: (msg: string, level?: 'ok' | 'warn' | 'err' | '') => void;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );

  const handleCopy = useCallback(() => {
    navigator.clipboard
      ?.writeText(url)
      .then(() => {
        setCopied(true);
        onStatus('Share link copied.', 'ok');
        if (timer.current) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => onStatus('Could not copy link.', 'err'));
  }, [url, onStatus]);

  return (
    <button
      type="button"
      onClick={handleCopy}
      title="Copy a shareable link to this song"
      aria-label="Copy share link"
      className={
        'text-[10px] uppercase tracking-[0.18em] px-2.5 py-1.5 rounded-sm border ' +
        (copied
          ? 'bg-zinc-800 border-cyan-300/60 text-cyan-300'
          : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:text-cyan-300 hover:border-cyan-300/50')
      }
    >
      {copied ? '✓ Copied' : '🔗 Copy link'}
    </button>
  );
}
